import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

export interface CreativeGroup {
  clicks: number;
  id: string;
  impressions: number;
  // Identifies the group — creatives can share a name, so this, not `name`, is
  // what tells two of them apart.
  key: string;
  name: string;
}

interface CreativeSizeGroup {
  clicks: number;
  impressions: number;
  size: string;
}

// Groups rows by creative_id (with creative_name fallback), summing impressions
// and clicks. Returns sorted desc by impressions so top-N slicing is .slice(0, n).
const UNKNOWN_LABEL = TEXT.campaignOverview.labels.unknown;

export const groupByCreative = (
  rows: PerformanceReportRow[]
): CreativeGroup[] => {
  const map = rows.reduce((acc, row) => {
    // Namespace the key so IDs and names can never collide, and a falsy/empty
    // ID always falls through to the name bucket rather than grouping under "".
    const key = row.creative_id
      ? `id:${row.creative_id}`
      : `name:${row.creative_name ?? UNKNOWN_LABEL}`;
    const name = row.creative_name ?? UNKNOWN_LABEL;
    const existing = acc.get(key) ?? {
      clicks: 0,
      id: row.creative_id ?? "",
      impressions: 0,
      key,
      name,
    };
    return acc.set(key, {
      clicks: existing.clicks + (row.clicks ?? 0),
      id: existing.id || (row.creative_id ?? ""),
      impressions: existing.impressions + (row.impressions ?? 0),
      key,
      name: existing.name || name,
    });
  }, new Map<string, CreativeGroup>());
  return [...map.values()].sort(
    (rowA, rowB) => rowB.impressions - rowA.impressions
  );
};

// Groups rows by creative_size, summing impressions and clicks. Returns sorted
// desc by impressions.
// The first `limit` creatives (the list is already ordered), plus the leader
// (by group key) when it ranks lower — so the Top performers detail always
// shows the creative its tile names, however few impressions it has next to
// the top ten.
export const selectTopCreatives = (
  groups: CreativeGroup[],
  limit: number,
  leaderKey: string | null = null
): { creatives: CreativeGroup[]; leaderAdded: boolean } => {
  const top = groups.slice(0, limit);
  const leader = leaderKey
    ? groups.slice(limit).find(({ key }) => key === leaderKey)
    : undefined;
  return leader
    ? { creatives: [...top, leader], leaderAdded: true }
    : { creatives: top, leaderAdded: false };
};

export const groupByCreativeSize = (
  rows: PerformanceReportRow[]
): CreativeSizeGroup[] => {
  const map = rows.reduce((acc, row) => {
    const key = row.creative_size ?? UNKNOWN_LABEL;
    const existing = acc.get(key) ?? { clicks: 0, impressions: 0 };
    return acc.set(key, {
      clicks: existing.clicks + (row.clicks ?? 0),
      impressions: existing.impressions + (row.impressions ?? 0),
    });
  }, new Map<string, { clicks: number; impressions: number }>());
  return [...map.entries()]
    .map(([size, { clicks, impressions }]) => ({ clicks, impressions, size }))
    .sort((rowA, rowB) => rowB.impressions - rowA.impressions);
};

// Weighted CTR (clicks / impressions × 100) — avoids the error of averaging
// per-row rate values, which would under-weight high-impression creatives.
export const weightedCtr = (clicks: number, impressions: number): number =>
  impressions > 0 ? (clicks / impressions) * 100 : 0;
